import { parseRepositoryPuppyNodeWhere } from './repository-puppy-node-where-path'

/**
 * 仓库 Puppy 节点 occurrence 身份解析输入。
 */
export interface ResolveRepositoryPuppyNodeOccurrenceIdentityInput {
  /** `_assembly.json` 中保存的 Puppy occurrence 定位路径。 */
  readonly nodeWhere: unknown
  /** 当前实际加载的仓库 `.puppy` 文件路径。 */
  readonly packageFileId: string
}

/**
 * 仓库 Puppy 节点 occurrence 身份解析结果。
 */
export type RepositoryPuppyNodeOccurrenceIdentityResult =
  | {
    /** 已从当前包路径精确恢复持久化身份。 */
    readonly ok: true
    /** 包内稳定装配节点 ID。 */
    readonly assemblyNodeId: string
  }
  | {
    /** 当前输入无法证明持久化身份。 */
    readonly ok: false
    /** 无法恢复的确定原因。 */
    readonly reason: 'invalid-node-where' | 'invalid-package-file-id' | 'not-found'
  }

/**
 * 从当前仓库 Puppy 包的显式桥接路径恢复 occurrence 持久化身份。
 *
 * 该入口只承认 `node_where + packageFileId` 协议，不接收 `nodeKey`、实例 ID 或 Three.js UUID
 * 作为候选，避免结构身份和运行时身份在版本切换、提交及重载时污染持久化身份。
 *
 * @param input 原始 node_where 与当前 Puppy 包路径。
 * @returns 精确恢复的 assemblyNodeId，或可判定的失败原因。
 */
export function resolveRepositoryPuppyNodeOccurrenceIdentity(
  input: ResolveRepositoryPuppyNodeOccurrenceIdentityInput,
): RepositoryPuppyNodeOccurrenceIdentityResult {
  if (typeof input.nodeWhere !== 'string' || !input.nodeWhere.trim()) {
    return { ok: false, reason: 'invalid-node-where' }
  }

  let assemblyNodeId: string | null
  try {
    assemblyNodeId = parseRepositoryPuppyNodeWhere(input.nodeWhere, input.packageFileId)
  } catch {
    return { ok: false, reason: 'invalid-package-file-id' }
  }

  return assemblyNodeId
    ? { ok: true, assemblyNodeId }
    : { ok: false, reason: 'not-found' }
}
